import { NavigateOptions, useNavigate } from 'react-router';
import { AccessTokenType, AuthResponseType } from '@/types/sharedTypes';
import { getAuthNextStepPath } from '../const/const';
import { authTokenService } from '../../shared/services/authTokenService';
import { useLogin } from './useLogin';
import { decodeToken, getAccessToken } from '@/helpers/tokenHelpers';
import { getSelectRolePath } from '@/bundle/Auth/LoginFlow/SelectRolePage/path/path';
import { REDIRECT_URL_KEY } from '@/const/shared';
import { sessionStorageService } from '@/helpers/storageHelpers';
import { hasMultipleRoles } from '@/helpers/userHelpers';

export const useAuthNavigateToStep = () => {
  const navigate = useNavigate();
  const { login } = useLogin();

  const navigateToStep = async (body: AuthResponseType, navigateOptions?: NavigateOptions) => {
    // User is not authenticated - parse JWT token and navigate to next step
    if (body?.auth_token) {
      authTokenService.setTokens({ access: body?.auth_token });

      const nextStepPath = getAuthNextStepPath(body?.auth_token);

      return navigate(nextStepPath, navigateOptions);
    }

    // User is authenticated -> continue login flow
    if (body?.access) {
      // Check if user has multiple roles and show select role page to continue as a selected role
      const tokenData = decodeToken<AccessTokenType>(body?.access);
      const { token } = getAccessToken(body);
      const hasActiveMultiRoles = hasMultipleRoles(tokenData.active_roles);

      if (hasActiveMultiRoles) {
        authTokenService.setTokens(token);

        const previousLocation = sessionStorageService.get(REDIRECT_URL_KEY);

        // If user has previous location then probably he redirected from email notification.
        // In such case we don't need to show select role page.
        if (previousLocation) {
          return login(body);
        }

        return navigate(getSelectRolePath());
      }

      return login(body);
    }
  };

  return navigateToStep;
};
